import { Shirt, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CardTone, ProductCard } from '@/types/api';
import { REPRESENTATIVE_LABEL, REPRESENTATIVE_PHOTOS, representativeSrc, representativeSrcSet } from './representativePhotos';

/** Soft tints used behind the placeholder (admin "placeholder colour"). Full class strings for Tailwind. */
export const TONE_CLASSES: Record<CardTone, string> = {
  slate: 'bg-[#e9edf2]',
  mint: 'bg-[#e4efe9]',
  navy: 'bg-[#dfe5ed]',
  sand: 'bg-[#efebe2]',
  sky: 'bg-[#e3eaf3]',
  sage: 'bg-[#e6ece4]',
  white: 'bg-[#f1f0ec]',
  rose: 'bg-[#f2e7e5]',
  cream: 'bg-[#f3eee3]',
};

/**
 * Shows the first uploaded product photo. Without one, a labelled representative photo of the product
 * type is shown where available; otherwise an honest "made to order" tile.
 */
export function ProductVisual({ product, className, eager }: { product: Pick<ProductCard, 'slug' | 'name' | 'cardTone' | 'images' | 'category'>; className?: string; eager?: boolean }) {
  const image = product.images[0];
  if (image) {
    return (
      <div className={cn('overflow-hidden bg-surface', className)}>
        <img src={image.url} alt={image.altText} loading={eager ? 'eager' : 'lazy'} decoding="async" className="zoom-on-hover h-full w-full object-cover" />
      </div>
    );
  }
  const representative = REPRESENTATIVE_PHOTOS[product.slug];
  if (representative) {
    return (
      <div className={cn('relative overflow-hidden bg-surface', className)}>
        <img
          src={representativeSrc(representative.file, 800)}
          srcSet={representativeSrcSet(representative.file)}
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          width={1200}
          height={960}
          alt={representative.alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="zoom-on-hover h-full w-full object-contain"
        />
        <span className="absolute bottom-2 left-2 rounded-[4px] bg-white/90 px-2 py-0.5 text-[10.5px] font-medium text-muted">{REPRESENTATIVE_LABEL[representative.kind ?? 'representative']}</span>
      </div>
    );
  }
  const IconC = product.category.division === 'SPORTS' ? Trophy : Shirt;
  return (
    <div className={cn('weave relative flex flex-col items-center justify-center', TONE_CLASSES[product.cardTone] ?? TONE_CLASSES.slate, className)} role="img" aria-label={`${product.name} — product photo not yet available`}>
      <span className="grid h-16 w-16 place-items-center rounded-full border border-primary/15 text-primary/45 transition-transform duration-500 group-hover:scale-105">
        <IconC className="h-7 w-7" strokeWidth={1.4} aria-hidden />
      </span>
      <span className="mt-4 text-[11px] font-semibold tracking-[0.16em] text-muted uppercase">Made to order</span>
    </div>
  );
}
